import React from 'react'
import { Button } from './button'

export interface PaginationLabels {
  range: (values: { start: number; end: number; total: number }) => React.ReactNode
  previous: string
  next: string
}

export interface PaginationProps {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  labels?: PaginationLabels
}

const DEFAULT_LABELS: PaginationLabels = {
  range: ({ start, end, total }) => `Showing ${start}–${end} of ${total}`,
  previous: 'Previous',
  next: 'Next',
}

/**
 * Paging controls only. Callers choose client-side or server-side data access;
 * high-volume tables such as orders must use server-side pagination.
 */
export function Pagination({ page, pageSize, total, onPageChange, labels = DEFAULT_LABELS }: PaginationProps) {
  if (total === 0) return null
  const start = page * pageSize + 1
  const end = Math.min(total, (page + 1) * pageSize)
  const lastPage = Math.ceil(total / pageSize) - 1

  return (
    <div className="mt-4 flex items-center gap-2.5">
      <span className="font-mono text-sm tabular-nums text-muted">{labels.range({ start, end, total })}</span>
      <Button variant="secondary" size="sm" className="ml-auto" disabled={page === 0} onClick={() => onPageChange(page - 1)}>
        {labels.previous}
      </Button>
      <Button variant="secondary" size="sm" disabled={page >= lastPage} onClick={() => onPageChange(page + 1)}>
        {labels.next}
      </Button>
    </div>
  )
}

/** Clamp a page index after the filtered total shrinks. */
export function clampPage(page: number, pageSize: number, total: number): number {
  if (total === 0) return 0
  return Math.min(page, Math.ceil(total / pageSize) - 1)
}
